import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchProject } from "../lib/api";
import { Markdown } from "../components/mdx";

export const Route = createFileRoute("/projects_/$slug")({
  component: ProjectDetail,
});

// Tour-guide project detail page, structurally following
// design-reference/projects-clox.html: breadcrumb, status pill, then a
// sequence of numbered "stages" (0x00 why built, 0x01 my role, 0x02
// hardest problem, 0x03 status, 0x04 lessons learned). Every stage is
// generic over ProjectDetail's nullable tour-guide fields — a stage only
// renders when its field is present, so this works for any featured
// project, not just Clox.
function ProjectDetail() {
  const { slug } = Route.useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["project", slug],
    queryFn: () => fetchProject(slug),
  });

  interface Stage {
    title: string;
    body: string;
    verboseNote?: string | null;
    listClassName?: string;
  }

  const rawStages: Array<Stage | null> = data
    ? [
        data.whyBuilt
          ? { title: "why I built this", body: data.whyBuilt, verboseNote: data.verboseNote }
          : null,
        data.myRole ? { title: "my role", body: data.myRole } : null,
        data.hardestProblem ? { title: "the hardest problem", body: data.hardestProblem } : null,
        data.statusDetails ? { title: "status", body: data.statusDetails } : null,
        data.lessonsLearned
          ? { title: "lessons learned", body: data.lessonsLearned, listClassName: "lesson-list" }
          : null,
      ]
    : [];
  const stages: Stage[] = rawStages.filter((s): s is Stage => s !== null);

  return (
    <>
      <p className="breadcrumb">
        <Link to="/projects">projects()</Link> / {slug}
      </p>

      {isLoading && <p className="section-sub">// loading...</p>}
      {isError && <p className="section-sub">// could not reach the api</p>}

      {data && (
        <>
          <p className="frame-label">
            frame <b>#2.1</b> — projects::{slug}()
          </p>
          <h1>{data.signature}</h1>

          <div className="meta-row">
            {data.stack && (
              <span>
                <b>stack</b> {data.stack}
              </span>
            )}
            {data.projectType && (
              <span>
                <b>type</b> {data.projectType}
              </span>
            )}
            {data.timeframe && (
              <span>
                <b>timeframe</b> {data.timeframe}
              </span>
            )}
            {data.url && (
              <a href={data.url} target="_blank" rel="noreferrer">
                source ↗
              </a>
            )}
          </div>

          {data.statusLabel && (
            <div className="status-pill">
              <span className="dot" />
              status: {data.statusLabel}
            </div>
          )}

          {stages.map((stage, i) => (
            <div className="stage" key={stage.title}>
              <div className="stage-head">
                <span className="addr">0x{String(i).padStart(2, "0")}</span>
                <h2>{stage.title}</h2>
              </div>
              <Markdown source={stage.body} listClassName={stage.listClassName} />
              {stage.verboseNote && (
                <p className="verbose-only" style={{ fontSize: "12.5px", color: "var(--mute)" }}>
                  -v: {stage.verboseNote}
                </p>
              )}
            </div>
          ))}

          <div className="links-row">
            <Link to="/projects" className="cta">
              ← back to projects
            </Link>
            {data.url && (
              <a href={data.url} target="_blank" rel="noreferrer" className="cta primary">
                view source ↗
              </a>
            )}
          </div>
        </>
      )}
    </>
  );
}
